import {
  DashboardOverviewTasksQueryVariables,
  FilterGroupingEnum,
  Task,
  TaskFilterInputObject,
  TaskScopeNameEnum,
  useDashboardOverviewTasksQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { Option } from '@lib/interfaces/form';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters, {
  FilterSchemaPredicateType,
} from '@lib/utils/createSearchFilters';
import getTaskCounterData from '@lib/utils/getTaskCounterData';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { ListQueryHookProps } from '../types';

export default function useDashboardOverviewTasksQueryHook({
  filterParams,
  paginationParams,
  searchQuery,
  skip,
  tabParam,
  defaultMinDate,
  defaultMaxDate,
  withIntervention = false,
}: ListQueryHookProps & {
  defaultMinDate?: string;
  defaultMaxDate?: string;
  // Selects `Task.intervention` — needs INTERVENTIONS_MANAGEMENT read.
  withIntervention?: boolean;
}) {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const period = _get(filterParams, TasksFilterFieldNames.Period) as string[];
  const periodStart = period?.[0];
  const periodEnd = period?.[1];
  const filterData = _get(
    filterParams,
    TasksFilterFieldNames.Filter,
  ) as object[];
  const tasksQueryVariables: DashboardOverviewTasksQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    scope: { name: tabParam as TaskScopeNameEnum },
    withIntervention,
  };

  // Adjust parameters based on scope
  switch (tabParam) {
    case TaskScopeNameEnum.FailureUnsolved:
    case TaskScopeNameEnum.WarningUnsolved:
      tasksQueryVariables.includeCompletion = true;
      tasksQueryVariables.includeStatus = true;
      break;
    case TaskScopeNameEnum.DashboardAll:
    case TaskScopeNameEnum.Solved:
    case TaskScopeNameEnum.Success:
    default:
      tasksQueryVariables.includeCompletion = true;
      break;
  }

  const filters: TaskFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'siteName' },
        { fieldName: 'siteAreaName' },
        { fieldName: 'siteCode' },
        { fieldName: 'siteAreaCode' },
        { fieldName: 'priority', predicateType: FilterSchemaPredicateType.Eq },
      ]),
    );
  }

  const periodStartValue = periodStart || defaultMinDate;
  const periodEndValue = periodEnd || defaultMaxDate;
  if (periodStartValue && periodEndValue) {
    filters.push({
      completedAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { gteq: periodStartValue },
        filterGroup: 'completedAt',
      },
    });
    filters.push({
      deadlineAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { gteq: periodStartValue },
        filterGroup: 'deadlineAt',
      },
    });
    filters.push({
      completedAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { lteq: periodEndValue },
        filterGroup: 'completedAt',
      },
    });
    filters.push({
      deadlineAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { lteq: periodEndValue },
        filterGroup: 'deadlineAt',
      },
    });
  }

  if (filterData) {
    const siteIdsForFilter: string[] = [];
    const siteAreaIdsForFilter: string[] = [];
    filterData.forEach((item) => {
      const site = _get(item, 'site', {}) as Option;
      const siteAreaIds = _get(item, 'siteAreaIds', []) as Option[];
      if (site && siteAreaIds.length === 0) {
        siteIdsForFilter.push(site.value as string);
      } else if (siteAreaIds.length > 0) {
        siteAreaIds.forEach(({ value }) =>
          siteAreaIdsForFilter.push(value as string),
        );
      }
    });
    if (siteIdsForFilter.length) {
      filters.push({
        siteId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { in: siteIdsForFilter },
          filterGroup: 'siteAndArea',
        },
      });
    }
    if (siteAreaIdsForFilter.length) {
      filters.push({
        siteAreaId: {
          grouping: FilterGroupingEnum.Or,
          predicate: { in: siteAreaIdsForFilter },
          filterGroup: 'siteAndArea',
        },
      });
    }
  }
  if (filters.length) {
    tasksQueryVariables.filters = filters.filter(
      (filterItem) => !filterItem.deadlineAt,
    );
    tasksQueryVariables.activeTasksFilters = filters.filter(
      (filterItem) => !filterItem.completedAt,
    );
  }
  if (orderingField && orderingDirection) {
    tasksQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const tasksQuery = useDashboardOverviewTasksQuery({
    fetchPolicy: 'no-cache',
    variables: tasksQueryVariables,
    skip,
  });
  const {
    loading: tasksLoading,
    data,
    previousData,
    refetch: refetchTasks,
    error: tasksError,
  } = tasksQuery;
  const queryData = data || previousData;
  const { tasks, ...taskCounterData } = queryData || {};
  const tasksFirstLoading = tasksLoading && previousData === undefined;
  const metadata = _get(queryData, 'tasks.metadata');
  const totalTasksCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);
  return {
    refetchTasks,
    taskCounterData: getTaskCounterData(taskCounterData),
    tasks: _get(tasks, 'collection', []) as Task[],
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalPages,
    totalTasksCount,
  };
}
